import { useState } from 'react'

import { AlertCircle } from 'lucide-react'

import { Alert, AlertDescription } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'

type GenericProductModalProps = {
    onAddToCart: (product: { name: string; price: number; vat?: number }) => void
}

export function GenericProductModal({ onAddToCart }: GenericProductModalProps) {
    const [name, setName] = useState('')
    const [price, setPrice] = useState('')
    const [vat, setVat] = useState('21') // Default VAT is 21%
    const [error, setError] = useState('')
    const [isOpen, setIsOpen] = useState(false)

    const handleAddToCart = () => {
        if (!name.trim()) {
            setError('Por favor, introduce un nombre para el producto.')
            return
        }

        const parsedPrice = parseFloat(price)
        if (isNaN(parsedPrice) || parsedPrice <= 0) {
            setError('Por favor, introduce un precio válido y positivo.')
            return
        }

        const parsedVat = parseInt(vat)
        if (isNaN(parsedVat) || parsedVat < 0 || parsedVat > 100) {
            setError('Por favor, introduce un IVA válido entre 0 y 100.')
            return
        }

        onAddToCart({ name, price: parsedPrice, vat: parsedVat })
        setName('')
        setPrice('')
        setVat('21')
        setError('')
        setIsOpen(false)
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button className='bg-green-600 text-white hover:bg-green-700'>Producto Genérico</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Añadir Producto Genérico</DialogTitle>
                </DialogHeader>
                <div className='space-y-4'>
                    <div>
                        <Label htmlFor='name'>Nombre del Producto</Label>
                        <Input
                            id='name'
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder='Nombre del producto'
                        />
                    </div>
                    <div>
                        <Label htmlFor='price'>Precio (€)</Label>
                        <Input
                            id='price'
                            type='number'
                            value={price}
                            onChange={e => setPrice(e.target.value)}
                            placeholder='0.00'
                            step='0.01'
                            min='0'
                        />
                    </div>
                    <div>
                        <Label htmlFor='vat'>IVA (%)</Label>
                        <Input
                            id='vat'
                            type='number'
                            value={vat}
                            onChange={e => setVat(e.target.value)}
                            placeholder='21'
                            step='1'
                            min='0'
                            max='100'
                        />
                    </div>
                    {error && (
                        <Alert variant='destructive'>
                            <AlertCircle className='h-4 w-4' />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    <div className='flex justify-end space-x-2'>
                        <Button variant='outline' onClick={() => setIsOpen(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleAddToCart}>Añadir al Carrito</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
